/**
 * Every factual claim the landing page makes, in one place.
 *
 * ─── Why the numbers live here and not inline in the markup ─────────────────
 * A landing page is a promise. "190 items" printed into a template literal is
 * a number nobody will ever check again, and content is added and re-tiered in
 * this repo constantly — within a month it is simply false, and nothing
 * anywhere goes red. Every number below is instead a FLOOR, checked against
 * the real content by `landing-copy.test.ts`, which counts the seed source and
 * the design data directly. Adding content can never break it; removing enough
 * content to make the page lie will.
 *
 * That is also why the printed strings say "180+" rather than "190". An exact
 * count is a hostage to the next commit; a floor with the real figure
 * comfortably above it stays true while the game grows.
 *
 * ─── Nothing here may describe something that does not exist ────────────────
 * Checked against the code, not the design docs, which run ahead of it. In
 * particular: there is no ranked season and no leaderboard in this game's code
 * (`arena.service.ts` says so in as many words — no game code ever calls
 * `LeaderboardService.registerCategory`), so the PvP section does not mention
 * either. Mastery perks are marked PLANNED in `specialization-catalog.ts` and
 * are likewise absent.
 */

/**
 * The floors. Each one is `<=` the real count today; the test says where each
 * real count is read from.
 */
export const CONTENT_FACTS = {
  /** `ItemDef` rows in `backend/prisma/seed.ts` (190 today). */
  items: 180,
  /**
   * Armed pose families in `LOCOMOTION_ARMS_CLIP` — pistol, rifle, knife,
   * blunt, axe. Five, and `unarmed` is not one of them.
   *
   * Janeks: *"I think we have more, nevertheless say 'multiple' and mention
   * calibers, and weapon families number."* He is right that a count of
   * SEEDED weapons understates the game — there are 40 weapon GLBs in
   * `public/models/weapons/` against 8 `ItemDef` rows — so the page no longer
   * prints either number. A family is the honest unit: it is what decides how
   * a weapon is held, carried and swung, it is checkable in the animation
   * code, and it does not move every time a model is wired to an item.
   */
  weaponFamilies: 5,
  /** `kind: 'ARMOUR'` (5 today). */
  armour: 5,
  /** `kind: 'AMMO'` (5 today). */
  ammoTypes: 5,
  /** `kind: 'TOOL'` (14 today). */
  tools: 14,
  /** `RECIPES` in `backend/prisma/content/recipes.ts` (9 today). */
  recipes: 9,
  /** The `Specialization` enum in `schema.prisma` (5 today). */
  vocations: 5,
  /** `HOME_LEVELS` + the base level in `backend/src/game/content/home-levels.ts` (6 today). */
  hideoutLevels: 6,
  /** `ATTRIBUTE_CODES` in `backend/src/game/character/character.types.ts` (5 today). */
  attributes: 5,
  /** Zones in `backend/prisma/content/world-zones.ts` (26 today). */
  districts: 26,
  /** Battleground maps in `MAP_REGISTRY` (2 today). */
  battlegrounds: 2,
  /** `STORY_CHAIN` steps in `backend/prisma/content/story-chain.ts` (31 today). */
  storySteps: 30,
  /** `ALL_PERIODIC_QUESTS` — daily + weekly (9 today: 6 daily, 3 weekly). */
  periodicQuests: 9,
  /** Authored dialog trees in `backend/prisma/content/dialog-trees.ts` (9 today). */
  dialogTrees: 9,
  /** `CharacterConfig.levelCap`'s default — see `encounter.types.ts`, which pins it at 30. Exact, not a floor: a cap that grows makes the page understate, never lie. */
  levelCap: 30,
} as const;

export const BRAND = {
  name: 'After Humanity',
  /** Above the title. Janeks: *"moto"*. */
  motto: 'The war ended. Nobody won.',
  /** Under the title, the one-sentence pitch. Straight from `design/gdd/core-loop.md`. */
  pitch:
    'A prep-and-extract scavenger RPG. Run the ruins for what is left, get out before dark, '
    + 'and turn the haul into a life worth having in New Ashford.',
  settlement: 'New Ashford',
} as const;

export interface LandingStat {
  value: string;
  label: string;
}

/**
 * How a section renders its three numbers.
 *
 * Re-cycled when Gear moved to the top of the page (Janeks: *"Put the weapon
 * block right after the Hero"*) — the styles alternate by POSITION, so
 * reordering the sections means reassigning them or two neighbours end up
 * wearing the same one.
 *
 * Janeks: *"change the view of .lp-stat (3 different styles - current but
 * better, Tapes from game styles, Naked (empty))"*. Cycled down the page so no
 * two adjacent sections use the same one — six sections, three styles, twice
 * each.
 */
export type StatStyle = 'plate' | 'tape' | 'naked';

export interface LandingSection {
  /** Anchor id — also what the header menu links to. */
  id: string;
  /** Short label for the header menu. */
  nav: string;
  heading: string;
  body: string;
  /** Bulleted specifics. Each one must be a thing the game does today. */
  points: string[];
  /** Big numbers rendered as a row of tiles. */
  stats: LandingStat[];
  statStyle: StatStyle;
  /**
   * A lazily loaded 3D preview beside the copy, or absent for a section that
   * has none. See `landing-preview.ts` — the modules are dynamically imported
   * and never run on a narrow screen or under reduced motion.
   */
  preview?: 'survivor' | 'weapon';
}

const f = CONTENT_FACTS;

export const SECTIONS: LandingSection[] = [
  {
    id: 'content',
    nav: 'Gear',
    heading: 'Everything in the ruins is worth something to someone',
    body:
      `${f.items}+ distinct items, and almost none of them are loot for loot's sake. Scrap feeds `
      + `the workbench. Cloth becomes a vest. A broken watch is worth exactly what a trader will `
      + `give you for it. Weight is the real constraint — your pack fills long before your nerve `
      + `runs out, and everything you are carrying is what you lose if you die out there.`,
    points: [
      `Multiple weapon families — pistols, rifles, blades, blunt and axes, ${f.weaponFamilies} in all — `
        + `each carried, held and swung differently, across ${f.ammoTypes} calibres of ammunition`,
      `${f.armour} armour slots that trade protection against the weight and the penalty they cost you`,
      `${f.tools} tools, plus materials, food, medicine and salvage that exists purely to be sold`,
      'Equipped gear survives your death. The pack does not.',
    ],
    stats: [
      { value: `${f.items}+`, label: 'Items' },
      { value: String(f.weaponFamilies), label: 'Weapon families' },
      { value: String(f.ammoTypes), label: 'Calibres' },
    ],
    statStyle: 'naked',
    preview: 'weapon',
  },
  {
    id: 'progression',
    nav: 'Progress',
    heading: 'Nobody starts as anything',
    body:
      `You begin in the Sleeping Corners — a shared bunk in someone else's settlement — with `
      + `${f.attributes} attributes at their floor and no claim on anything. Every level earns `
      + `points to spend on the survivor you actually want, and the derived stats follow: what you `
      + `can carry, how fast you move, how well you shoot, how quickly you learn. There is no `
      + `respec and no fixed build.`,
    points: [
      `${f.attributes} attributes — Strength, Intellect, Stamina, Agility, Perception — spent by hand, never auto-assigned`,
      'Every stat you see is derived on read from those five plus your gear, so a swapped vest changes your run speed immediately',
      `A ${f.storySteps}-step story chain through the settlement's people, plus daily and weekly work that resets on its own`,
      `${f.periodicQuests} recurring contracts and ${f.dialogTrees} written characters who remember what you have done for them`,
    ],
    stats: [
      { value: String(f.levelCap), label: 'Levels' },
      { value: String(f.attributes), label: 'Attributes' },
      { value: `${f.storySteps}+`, label: 'Story steps' },
    ],
    statStyle: 'plate',
  },
  {
    id: 'run',
    nav: 'The run',
    heading: 'Daylight is the only thing you cannot borrow against',
    body:
      `A raid is not a level you walk through. You pick a focus, spend the energy, and your `
      + `survivor works the map while three pressures close in at once: the light going, the pack `
      + `filling, and whatever the city sends at you. Every one of them folds into the same `
      + `question, asked again and again until you answer it wrong.`,
    points: [
      'Prep decides the run — gear, consumables and focus are chosen before you leave, not during',
      'Pick what you are out for: food, scrap, a bounty, or just to see what is there',
      'Points of interest you can drop into on foot, or skip and keep the daylight',
      'Push or extract. Extract and the haul is banked; die and the pack is gone with you',
    ],
    stats: [
      { value: '4', label: 'Raid focuses' },
      { value: '3', label: 'Pressures' },
      { value: '1', label: 'Question' },
    ],
    statStyle: 'tape',
  },
  {
    id: 'people',
    nav: 'The people',
    heading: 'Somebody was already here when you arrived',
    body:
      `${BRAND.settlement} is not a menu with faces on it. The overseer keeps the work rota, the `
      + `medic wants her bandages back, and the scrap trader has opinions about what you brought `
      + `him. What you get out of this place is decided by what you have already done for the `
      + `people in it.`,
    points: [
      `${f.dialogTrees} written characters with real conversations, not one-line vendors`,
      'Stalls, a medbay, a bank and a black market — each run by somebody with a name',
      'Story moves person to person: the next step does not open until the last one earned you their attention',
      'Quests and standing are tracked per person, so a favour is remembered and so is a refusal',
    ],
    stats: [
      { value: String(f.dialogTrees), label: 'Characters' },
      { value: String(f.periodicQuests), label: 'Contracts' },
      { value: `${f.storySteps}+`, label: 'Story steps' },
    ],
    statStyle: 'naked',
  },
  {
    id: 'hideout',
    nav: 'Hideout',
    heading: 'Earn the roof, then earn the room under it',
    body:
      `A bunk in the Corners is not a home. Standing in ${BRAND.settlement} buys you a door that `
      + `locks, and from there the house itself is the progression: ${f.hideoutLevels} levels, each `
      + `one a different building on the same plot, each one unlocking storage, benches and work `
      + `the last one could not hold.`,
    points: [
      `${f.hideoutLevels} hideout levels, every one a real model swap on the settlement map — you can see how far along a place is by looking at it`,
      `Pick one of ${f.vocations} vocations — Scavenging, Bounty Hunting, Engineering, Cooking, Tailoring — and the bench stocks itself accordingly`,
      `${f.recipes} recipes gated behind house level and vocation, each a real timed job you come back for`,
      'Hideout storage is separate from your pack, and nothing in it is ever at risk',
    ],
    stats: [
      { value: String(f.hideoutLevels), label: 'House levels' },
      { value: String(f.vocations), label: 'Vocations' },
      { value: String(f.recipes), label: 'Recipes' },
    ],
    statStyle: 'plate',
  },
  {
    id: 'conflict',
    nav: 'Conflict',
    heading: 'The city, and the people who also want it',
    body:
      `Raids run out across ${f.districts} districts of a dead city, in three tiers of how badly it `
      + `wants you gone — predators, bandits and hazards, weighted by how deep you have gone. When `
      + `you want it settled against a person instead of a place, the arena is one queue away.`,
    points: [
      `${f.districts} districts across three threat tiers, each with its own encounter pool`,
      'Raids resolve server-side: prep, focus and nerve decide them, not reflexes or your connection',
      'Points of interest you can drop into directly and clear on foot, in the same engine the settlement runs in',
      `Arena duels matched by skill on ${f.battlegrounds} battlegrounds — the Pit and the Scrapyard`,
    ],
    stats: [
      { value: String(f.districts), label: 'Districts' },
      { value: '3', label: 'Threat tiers' },
      { value: String(f.battlegrounds), label: 'Battlegrounds' },
    ],
    statStyle: 'tape',
    preview: 'survivor',
  },
];

/**
 * Legal documents, by slug.
 *
 * These are the three `backend/prisma/content/legal-pages.ts` seeds, which are
 * the only slugs `GET /api/cms/legal/:slug` will ever answer. The endpoint
 * fails CLOSED — a missing row and a typo'd slug are the same 404 — so this
 * list existing does not guarantee the rows do, and the footer is built to
 * render nothing rather than a row of dead links when they are absent.
 */
export const LEGAL_SLUGS = ['privacy-policy', 'terms-of-service', 'cookie-policy'] as const;
